import { useEffect, useState } from 'react';
import { opsDb } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/components/Toast';
import { getSubscriptions, upsertSubscription, deleteSubscription } from '@/services/alerts';
import { INCIDENT_TYPES, SEVERITIES, type AlertSubscription } from '@/types';

interface Category { key: string; label: string }

const emptyForm = {
  label: '',
  types: [] as string[],
  categories: [] as string[],
  min_severity: '' as '' | 'info' | 'warning' | 'error',
  needs_dev_only: false,
};

export function SubscriptionsPage() {
  const { developer } = useAuth();
  const toast = useToast();
  const [subs, setSubs] = useState<AlertSubscription[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  async function reload() {
    if (!developer) return;
    setSubs(await getSubscriptions(developer.id));
  }

  useEffect(() => {
    void reload();
    opsDb().from('issue_categories').select('key,label').eq('active', true).order('sort')
      .then(({ data }) => setCats((data ?? []) as Category[]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [developer?.id]);

  function toggle(list: string[], v: string): string[] {
    return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
  }

  async function save() {
    if (!developer) return;
    setSaving(true);
    try {
      await upsertSubscription({
        developer_id: developer.id,
        label: form.label || null,
        is_active: true,
        match_types: form.types.length ? form.types : null,
        match_categories: form.categories.length ? form.categories : null,
        min_severity: form.min_severity || null,
        needs_dev_only: form.needs_dev_only,
      });
      setForm({ ...emptyForm });
      toast('Subscription added', 'success');
      await reload();
    } catch (e: any) { toast(e.message ?? 'Failed to save', 'error'); }
    finally { setSaving(false); }
  }

  async function toggleActive(s: AlertSubscription) {
    await upsertSubscription({ id: s.id, developer_id: s.developer_id, is_active: !s.is_active });
    await reload();
  }

  async function remove(id: string) {
    await deleteSubscription(id);
    toast('Subscription removed', 'info');
    await reload();
  }

  return (
    <div className="page">
      <h1>Subscriptions</h1>
      <p className="muted">Get alerted for incidents that match a rule. Dev-required incidents (needs_dev) always alert you regardless — manage that on Settings.</p>

      <div className="card">
        <h3>New rule</h3>
        <label>Label (optional)</label>
        <input className="input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Crashes in Egypt room" />

        <label>Incident types</label>
        <div className="chips-select">
          {INCIDENT_TYPES.map((t) => (
            <button key={t} type="button" className={`chip-btn ${form.types.includes(t) ? 'on' : ''}`} onClick={() => setForm({ ...form, types: toggle(form.types, t) })}>{t}</button>
          ))}
        </div>

        <label>Categories</label>
        <div className="chips-select">
          {cats.map((c) => (
            <button key={c.key} type="button" className={`chip-btn ${form.categories.includes(c.key) ? 'on' : ''}`} onClick={() => setForm({ ...form, categories: toggle(form.categories, c.key) })}>{c.label}</button>
          ))}
        </div>

        <div className="inline">
          <div>
            <label>Minimum severity</label>
            <select className="input" value={form.min_severity} onChange={(e) => setForm({ ...form, min_severity: e.target.value as any })}>
              <option value="">Any</option>
              {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <label className="checkline">
            <input type="checkbox" checked={form.needs_dev_only} onChange={(e) => setForm({ ...form, needs_dev_only: e.target.checked })} />
            Only when dev-required
          </label>
        </div>

        <button className="btn btn-primary" disabled={saving} onClick={() => void save()}>Add subscription</button>
      </div>

      <h3>Your rules</h3>
      {subs.length === 0 && <div className="muted">No subscriptions yet.</div>}
      <div className="list">
        {subs.map((s) => (
          <div key={s.id} className={`row ${s.is_active ? '' : 'row-off'}`}>
            <div className="row-main">
              <div className="row-msg">{s.label || 'Rule'}</div>
              <div className="row-meta muted">
                {(s.match_types ?? ['any type']).join(', ')} · {(s.match_categories ?? ['any category']).join(', ')} · ≥ {s.min_severity ?? 'any'}{s.needs_dev_only ? ' · dev-required only' : ''}
              </div>
            </div>
            <div className="row-actions inline">
              <button className="btn btn-sm btn-ghost" onClick={() => void toggleActive(s)}>{s.is_active ? 'Disable' : 'Enable'}</button>
              <button className="btn btn-sm btn-danger" onClick={() => void remove(s.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
